import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';
import {
  Box,
  Table,
  TableBody,
  TableCell as TC,
  TableHeader,
  TableRow,
} from 'grommet';
import ReactTooltip from 'react-tooltip';
import { Wifi, StatusWarning, Refresh, StatusDisabled } from 'grommet-icons';
import numeral from 'numeral';
import { styledComponentsTheme as theme } from '../../../styles/styledComponentsTheme';
import { BeaconChainValidator } from '../types';
import { Text } from '../../../components/Text';
import shortenAddress from '../../../utils/shortenAddress';
import { Button } from '../../../components/Button';
import { Paper } from '../../../components/Paper';
import { Link } from '../../../components/Link';
import {
  BEACONCHAIN_URL,
  PRICE_PER_VALIDATOR,
  TICKER_NAME,
  LYXER_TO_GWEI,
  MAX_EFFECTIVE_BALANCE,
  MIN_ACTIVATION_BALANCE,
} from '../../../utils/envVars';
import { isCompounding } from '../../../utils/credentialsType';
import { usePagination } from './PaginationBar';
import { TagIndicator, GreyTag } from '../../../components/TagIndicator';

const FakeLink = styled.span`
  color: blue;
  text-decoration: underline;
  cursor: pointer;
  display: inline;
`;

const TableCell = styled(TC)`
  display: flex;
  flex-wrap: wrap;
  text-align: center;
  align-content: center;
  justify-content: space-around;
`;

const ValidatorTable: React.FC<{
  validators: BeaconChainValidator[];
  setSelectedValidator: (validator: BeaconChainValidator) => void;
  handleConnect: () => void;
}> = ({ validators, setSelectedValidator, handleConnect }) => {
  const tableSize = 25;
  const { formatMessage } = useIntl();
  const { page, PaginationBar } = usePagination(
    Math.ceil(validators.length / tableSize)
  );

  const validatorStatus = (validator: BeaconChainValidator) => {
    const { status, isonline } = validator;

    switch (status) {
      case 'pending': {
        return (
          <div className="flex">
            <Refresh color="blueLight" />
            <Text className="ml10">
              <FormattedMessage defaultMessage="Pending" />
            </Text>
          </div>
        );
      }
      case 'slashing': {
        return (
          <div className="flex">
            <StatusWarning color={theme.red.light} />
            <Text className="ml10">
              <FormattedMessage defaultMessage="Slashing" />
            </Text>
          </div>
        );
      }
      case 'slashed': {
        return (
          <div className="flex">
            <StatusWarning color={theme.red.light} />
            <Text className="ml10">
              <FormattedMessage defaultMessage="Slashed" />
            </Text>
          </div>
        );
      }
      case 'exiting': {
        return (
          <div className="flex">
            <StatusWarning color="yellowDark" />
            <Text className="ml10">
              <FormattedMessage defaultMessage="Exiting" />
            </Text>
          </div>
        );
      }
      case 'exited': {
        return (
          <div className="flex">
            <StatusDisabled color={theme.gray.medium} />
            <Text className="ml10">
              <FormattedMessage defaultMessage="Exited" />
            </Text>
          </div>
        );
      }
      case 'active_offline': {
        return (
          <div className="flex">
            <Wifi color={theme.gray.medium} />
            <Text className="ml10">
              <FormattedMessage defaultMessage="Offline" />
            </Text>
          </div>
        );
      }
      case 'active_ongoing': {
        return isonline ? (
          <div className="flex">
            <Wifi color={theme.green.dark} />
            <Text className="ml10">
              <FormattedMessage defaultMessage="Online" />
            </Text>
          </div>
        ) : (
          <div className="flex">
            <Wifi color={theme.gray.medium} />
            <Text className="ml10">
              <FormattedMessage defaultMessage="Offline" />
            </Text>
          </div>
        );
      }
      default:
        return '';
    }
  };

  const validatorRows = React.useMemo(() => {
    return validators
      .filter(
        validator =>
          validator.status === 'active_ongoing' ||
          validator.status === 'active_offline'
      )
      .slice((page - 1) * tableSize, page * tableSize)
      .map(validator => {
        const alreadyToppedUp =
          (isCompounding(validator.withdrawalcredentials) &&
            validator.effectivebalance >=
              Number(MAX_EFFECTIVE_BALANCE * LYXER_TO_GWEI)) ||
          (!isCompounding(validator.withdrawalcredentials) &&
            validator.effectivebalance >=
              Number(MIN_ACTIVATION_BALANCE * LYXER_TO_GWEI));

        // No top-ups for exited or slashed validators:
        const statusIneligible =
          validator.status === 'slashed' || validator.status === 'exited';

        const toolTipText = () => {
          if (statusIneligible)
            return formatMessage({
              defaultMessage:
                'Validators that have exited or been slashed are not eligible for topping up',
            });
          if (alreadyToppedUp)
            return formatMessage(
              {
                defaultMessage:
                  "This validator's balance is at the effective maximum: {PRICE_PER_VALIDATOR} {TICKER_NAME}.",
              },
              { PRICE_PER_VALIDATOR, TICKER_NAME }
            );
          return formatMessage({
            defaultMessage:
              'You can improve the effective balance of this validator by topping up',
          });
        };

        return (
          <React.Fragment key={validator.pubkey}>
            <TableRow>
              <TableCell scope="col" border="bottom">
                <Link
                  primary
                  to={`${BEACONCHAIN_URL}/validator/${validator.pubkey}`}
                >
                  {shortenAddress(validator.pubkey)}
                </Link>
              </TableCell>
              <TableCell scope="col" border="bottom">
                <Text>{validatorStatus(validator)}</Text>
              </TableCell>
              <TableCell scope="col" border="bottom">
                <TagIndicator
                  value={`Type ${validator.withdrawalcredentials[3]}`}
                  colors={GreyTag}
                />
              </TableCell>
              <TableCell scope="col" border="bottom">
                <Text>
                  {`${numeral(validator.balance / LYXER_TO_GWEI).format(
                    '0.00000'
                  )} ${TICKER_NAME}`}
                </Text>
              </TableCell>
              <TableCell scope="col" border="bottom">
                <Text>
                  {`${numeral(
                    validator.effectivebalance / LYXER_TO_GWEI
                  ).format('0.00000')} ${TICKER_NAME}`}
                </Text>
              </TableCell>
              <TableCell data-tip={toolTipText()}>
                <Button
                  onClick={() => setSelectedValidator(validator)}
                  label={formatMessage({ defaultMessage: 'Top up' })}
                  disabled={statusIneligible}
                />
              </TableCell>
            </TableRow>
          </React.Fragment>
        );
      });
  }, [validators, setSelectedValidator, formatMessage, page]);

  return validators.length > 0 ? (
    <Paper
      style={{
        marginTop: '3rem',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-around',
      }}
    >
      <ReactTooltip />
      <Table>
        <TableHeader>
          <TableRow>
            <TableCell scope="col" border="bottom">
              <Text>
                <FormattedMessage defaultMessage="Public key" />
              </Text>
            </TableCell>
            <TableCell scope="col" border="bottom">
              <Text>
                <FormattedMessage defaultMessage="Status" />
              </Text>
            </TableCell>
            <TableCell scope="col" border="bottom">
              <Text>
                <FormattedMessage defaultMessage="Credentials type" />
              </Text>
            </TableCell>
            <TableCell scope="col" border="bottom">
              <Text>
                <FormattedMessage defaultMessage="True balance" />
              </Text>
            </TableCell>
            <TableCell scope="col" border="bottom">
              <Text>
                <FormattedMessage defaultMessage="Effective balance" />
              </Text>
            </TableCell>
          </TableRow>
        </TableHeader>
        <TableBody>{validatorRows}</TableBody>
      </Table>
      <PaginationBar />
    </Paper>
  ) : (
    <Box align="center" justify="center" className="mt40">
      <Text weight={600}>
        <FormattedMessage defaultMessage="No validators found for your connected wallet" />
      </Text>
      <Text className="mt20">
        <FormattedMessage
          defaultMessage="You can {changeYourWallet} to load validators for a different address."
          values={{
            changeYourWallet: (
              <FakeLink onClick={handleConnect}>
                <FormattedMessage defaultMessage="change your wallet" />
              </FakeLink>
            ),
          }}
          description="{changeYourWallet} is a link labeled 'change your wallet'"
        />
      </Text>
    </Box>
  );
};

export default ValidatorTable;
